import { GamificationTransaction } from "@axl-dev/school-sdk";
import { ColumnType } from "antd/lib/table";
import cn from "classnames";
import { useMemo } from "react";

import { SortingDirection } from "@app/shared/lib/enums/sorting-direction";
import { useSdk } from "@app/shared/lib/hooks";
import {
  DateTime,
  HelpText,
  Loc,
  Table,
  ValuePlaceholder,
} from "@app/shared/ui";

const GamificationTransactionContent: React.FC<{
  transaction: GamificationTransaction;
}> = ({ transaction }) => {
  return (
    <>
      {transaction.product && (
        <div>
          <Loc word="Gamification.transaction.purchase" default="Purchase" />
          &nbsp;
          <ValuePlaceholder
            value={transaction.product.title}
            type="unspecified"
          />
        </div>
      )}

      {transaction.achievement?.achievement && (
        <div>
          <Loc
            word="Gamification.transaction.achievement"
            default="Achievement"
          />
          &nbsp;
          <ValuePlaceholder
            value={transaction.achievement.achievement.title}
            placeholder={
              transaction.achievement.achievement.subtype && (
                <Loc
                  word={`Gamification.achievement.subtype.${transaction.achievement.achievement.subtype}`}
                  default={transaction.achievement.achievement.subtype}
                />
              )
            }
            type="unspecified"
          />
        </div>
      )}

      {transaction.achievement?.course && (
        <div>
          <Loc word="Global.course" default="Course" />
          &nbsp;
          <ValuePlaceholder
            value={transaction.achievement.course.name}
            type="unspecified"
          />
        </div>
      )}

      {transaction.achievement?.library && (
        <div>
          <Loc word="Global.library" default="Library" />
          &nbsp;
          <ValuePlaceholder
            value={transaction.achievement.library.name}
            type="unspecified"
          />
        </div>
      )}

      {transaction.burnedTransaction && (
        <div>
          <Loc word="Gamification.transaction.isBurned" default="Burning" />
        </div>
      )}

      {!transaction.achievement &&
        !transaction.product &&
        !transaction.burnedTransaction && (
          <div>
            <Loc
              word="Gamification.transaction.admin"
              default="Accrual from the administrator"
            />
          </div>
        )}
    </>
  );
};

export const GamificationTransactionTable: React.FC = () => {
  const sdk = useSdk();

  const columns = useMemo(() => {
    return [
      {
        title: <Loc word="Global.info" />,
        width: "40%",
        align: "right",
        render: (_text, item) => {
          return <GamificationTransactionContent transaction={item} />;
        },
      },
      {
        title: <Loc word="Global.date" />,
        width: "30%",
        sorter: true,
        dataIndex: "CreatedDate",
        render: (_text, item) => {
          return <DateTime date={item.createdDate} format="lll" />;
        },
      },
      {
        title: <Loc word="Gamification.transaction.points" default="Points" />,
        align: "right",
        sorter: true,
        dataIndex: "Points",
        render: (_text, item) => {
          return (
            <div className="flex justify-end gap-5">
              <div
                className={cn(
                  "fw-500",
                  item.type === "deposit" ? "color-success" : "color-danger",
                )}
              >
                {item.points * (item.type === "deposit" ? 1 : -1)}
              </div>
              {item.expiredDate && (
                <HelpText
                  tooltip
                  label={
                    <span>
                      <Loc word="Global.expires" />
                      &nbsp;
                      <DateTime date={item.expiredDate} format="ll" />
                    </span>
                  }
                />
              )}
            </div>
          );
        },
      },
    ] as ColumnType<GamificationTransaction>[];
  }, []);

  return (
    <Table
      defaultFilters={{ take: 25 }}
      fetchItemsByFilter={async (filters) => {
        const res = await sdk.gamification.transaction.fetch({
          ...filters,
          sortType: filters.sortType === SortingDirection.asc ? "Asc" : "Desc",
        });

        return {
          items: res.body.items,
          filters: {
            ...res.body.filter,
            sortType:
              res.body.filter.sortType === "Desc"
                ? SortingDirection.desc
                : SortingDirection.asc,
            pageTotal: sdk.gamification.transaction.filter.pageTotal,
            page: sdk.gamification.transaction.filter.page,
          },
        };
      }}
      columns={columns}
      hidePaginationOnePage
      rowBordered={false}
      size="small"
      scroll={{ x: "max-content" }}
    />
  );
};
